import { useEffect, useState } from "react";

// Admin-configured bundle pricing ("3 pay stubs for $19.99"). The app fetches
// the enabled bundles once and prices multi-document orders against them:
// the largest bundle the order qualifies for sets the price, extra copies
// beyond it are charged at the unit price, and a bundle can never cost more
// than the plain per-unit total.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

let cache = null;
let inflight = null;

export function loadBundles() {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = fetch(`${BACKEND_URL}/api/bundles`)
      .then((r) => r.json())
      .then((d) => { cache = d && d.success && Array.isArray(d.bundles) ? d.bundles : []; return cache; })
      .catch(() => { cache = []; return cache; });
  }
  return inflight;
}

export function bundlePricing(documentType, quantity, unitPrice, bundles) {
  const q = Math.max(0, Number(quantity) || 0);
  const unitTotal = +(q * unitPrice).toFixed(2);
  const matching = (bundles || []).filter((b) =>
    b && b.enabled !== false && Array.isArray(b.documentTypes) && b.documentTypes.includes(documentType) && b.count >= 2 && b.price > 0
  );
  const applied = matching.filter((b) => q >= b.count).sort((a, b) => b.count - a.count)[0] || null;
  let price = unitTotal;
  if (applied) price = Math.min(unitTotal, +(applied.price + Math.max(0, q - applied.count) * unitPrice).toFixed(2));
  const next = matching.filter((b) => q < b.count).sort((a, b) => a.count - b.count)[0] || null;
  const savings = +(unitTotal - price).toFixed(2);
  let nudge = null;
  if (applied && savings > 0) {
    nudge = `Bundle applied: ${applied.count} for $${applied.price.toFixed(2)}${q > applied.count ? ` + $${unitPrice.toFixed(2)} each after` : ""} (you save $${savings.toFixed(2)})`;
  } else if (next && q > 0 && next.price < next.count * unitPrice) {
    const more = next.count - q;
    nudge = `Add ${more} more${more === 1 ? "" : ""} to get ${next.count} for $${next.price.toFixed(2)} (save $${(next.count * unitPrice - next.price).toFixed(2)})`;
  }
  return { price, unitTotal, savings, applied, next, nudge };
}

export function useBundlePrice(documentType, quantity, unitPrice) {
  const [bundles, setBundles] = useState(cache || []);
  useEffect(() => {
    let on = true;
    loadBundles().then((b) => { if (on) setBundles(b); });
    return () => { on = false; };
  }, []);
  return bundlePricing(documentType, quantity, unitPrice, bundles);
}
